import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Lock, Linkedin, Globe } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "+1 (604) 908-4578";
const PHONE_HREF = "tel:+16049084578";
const ADDR_1 = "1864 West 1st Avenue";
const ADDR_2 = "Vancouver, Canada";

const TEAM = [
  {
    name: "Tessa Dubois-Phillips",
    title: "Investment Analyst",
    email: "Tessa@wintercove.com",
    linkedin: "https://www.linkedin.com/in/tessa-dubois-phillips-a6a783263/",
    testid: "contact-team-tessa",
  },
  {
    name: "Nicholas Dubois-Phillips",
    title: "Investment Analyst",
    email: "Nicholas@wintercove.com",
    linkedin: "https://www.linkedin.com/in/nicholas-dubois-phillips-16575b360",
    testid: "contact-team-nicholas",
  },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast("Please complete every field before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
      toast("Thank you. We will be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit. Please try again or email us directly.";
      toast(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-24 lg:py-32 border-t hairline overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_downside-first/artifacts/fln3kr2e_image.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Light fade so the sunset stays vivid on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
        <div className="absolute inset-0 bg-[#1f3a2e]/5 mix-blend-multiply" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">Contact</p>
            <p className="mt-2 text-[#5e6e63] text-lg leading-relaxed max-w-md font-light">
              We respond to qualified inquiries personally. No automated funnels,
              no mailing lists. Reach us directly, or send a short note.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={PHONE_HREF}
                data-testid="contact-phone-link"
                className="group flex items-start gap-4 text-[#1f3a2e]"
              >
                <Phone size={18} strokeWidth={1.25} className="mt-1 text-[#5e6e63]" />
                <div>
                  <p className="eyebrow mb-1">Phone</p>
                  <p className="text-[18px] font-light tracking-tight group-hover:text-[#5e6e63] transition-colors">
                    {PHONE}
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4 text-[#1f3a2e]">
                <MapPin size={18} strokeWidth={1.25} className="mt-1 text-[#5e6e63]" />
                <div>
                  <p className="eyebrow mb-1">Office</p>
                  <p className="text-[18px] font-light tracking-tight">
                    {ADDR_1}<br />
                    <span className="text-[#5e6e63]">{ADDR_2}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 text-[#1f3a2e]">
                <Mail size={18} strokeWidth={1.25} className="mt-1 text-[#5e6e63]" />
                <div className="w-full">
                  <p className="eyebrow mb-3">Contact us</p>
                  <p className="text-[#5e6e63] text-[14px] font-light leading-relaxed mb-5 max-w-md">
                    To reach a specific member of our team, use their email or LinkedIn below.
                  </p>
                  <ul className="space-y-5">
                    {TEAM.map((p) => (
                      <li key={p.testid} data-testid={p.testid} className="flex flex-col">
                        <span className="font-serif-display text-[20px] tracking-tight text-[#1f3a2e]">
                          {p.name}
                        </span>
                        <span className="eyebrow !text-[#5e6e63] mt-1" style={{ paddingLeft: 0 }}>{p.title}</span>
                        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
                          {p.email && (
                            <a
                              href={`mailto:${p.email}`}
                              data-testid={`${p.testid}-email`}
                              className="text-[15px] font-light text-[#1f3a2e] hover:text-[#5e6e63] transition-colors"
                            >
                              {p.email}
                            </a>
                          )}
                          <a
                            href={p.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${p.testid}-linkedin`}
                            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.2em] uppercase text-[#5e6e63] hover:text-[#1f3a2e] transition-colors"
                          >
                            <Linkedin size={13} strokeWidth={1.5} />
                            LinkedIn
                          </a>
                          {p.website && (
                            <a
                              href={p.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`${p.testid}-website`}
                              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.2em] uppercase text-[#5e6e63] hover:text-[#1f3a2e] transition-colors"
                            >
                              <Globe size={13} strokeWidth={1.5} />
                              Website
                            </a>
                          )}
                        </div>
                        {p.note && (
                          <p className="mt-3 text-[#5e6e63] text-[13px] font-light leading-relaxed max-w-md">
                            {p.note}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white/55 backdrop-blur-md p-8 lg:p-14 border hairline shadow-sm">
              {submitted ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <p className="eyebrow mb-6">Received</p>
                  <h3 className="font-serif-display text-3xl lg:text-4xl text-[#1f3a2e] font-light leading-tight">
                    Thank you. A member of our team will be in touch.
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="btn-secondary mt-10"
                  >
                    Send another note
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
                  <div>
                    <label htmlFor="contact-name" className="eyebrow block mb-3">Name</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      className="input-bare"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="eyebrow block mb-3">Email</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="name@example.com"
                      className="input-bare"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="eyebrow block mb-3">Message</label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Briefly tell us about your mandate or question"
                      className="input-bare resize-none"
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2 text-[#5e6e63] text-[13px] font-light">
                    <Lock size={14} strokeWidth={1.25} />
                    <span>
                      Your details are kept private. We never share or sell inquiry information.
                    </span>
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Send inquiry"}
                    <ArrowRight size={16} strokeWidth={1.25} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
